import React, { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { AppButton } from '../../components/general/AppButton/AppButton';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { logoutUser, signInWithOtt } from '../../redux/services/authService';
import dillLogoWithText from '../../assets/svg/dill_logo_with_text.svg';
import { buildSupplierPath } from '../../utils/helpers';

/**
 * This page is reachable directly, so every value below comes off the query string and is
 * attacker-chosen - the backend's own checks are not between an attacker and this code. A raw
 * string assigned to `window.location.href` runs a `javascript:` URL in Dill's origin, so a
 * return URL is only ever used when it is a Dill-relative path or an https one.
 */
const safeExternalUrl = (raw: string | null, fallback: string): string => {
  if (!raw) {
    return fallback;
  }
  // Relative to Dill, but not protocol-relative (`//evil.com` is a different origin)
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    return raw;
  }
  try {
    const url = new URL(raw);
    // `javascript:`/`data:` parse cleanly and differ only by protocol
    return url.protocol === 'https:' ? url.toString() : fallback;
  } catch {
    return fallback;
  }
};

const SSOCompletePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const hasExchangedRef = useRef(false);
  const hasLoggedOut = useRef(false);

  // Get SSO state from Redux
  const { ssoLoading, ssoError, user } = useAppSelector((state) => state.auth);

  // Check for error from backend redirect
  const backendError = searchParams.get('error');
  // Get the one-time token from URL
  const ott = searchParams.get('ott');
  // Not a script sink, but it is rendered on a Dill-branded page - keep it to real images
  const supplierLogoUrl = safeExternalUrl(searchParams.get('supplierLogoUrl'), '');

  // Logout current user when visiting SSO page
  useEffect(() => {}, [dispatch]);

  useEffect(() => {
    // If no OTT, don't attempt exchange
    if (!ott) {
      return;
    }

    // Prevent double exchange
    if (hasExchangedRef.current) {
      return;
    }

    const exchangeOtt = async () => {
      hasExchangedRef.current = true;
      sessionStorage.setItem('isSSO', 'true');
      await dispatch(logoutUser());
      await dispatch(signInWithOtt({ ott }));
    };

    if (ott && !ssoLoading && !hasExchangedRef.current) {
      exchangeOtt();
    }
  }, [searchParams, ott]);

  useEffect(() => {
    if (!hasExchangedRef.current) {
      sessionStorage.setItem('isSSO', 'true');
      hasLoggedOut.current = true;
      dispatch(logoutUser());
    }
  }, [dispatch]);

  // Navigate on successful sign-in
  useEffect(() => {
    if (hasExchangedRef.current && !ssoLoading && user) {
      navigate(buildSupplierPath(user?.linkedSupplierIds?.[0], '/customers'), { replace: true });
    }
  }, [ssoLoading, user, navigate]);

  // Determine error to display
  const displayError = backendError ? decodeURIComponent(backendError) : ssoError;
  const loginPageUrl = safeExternalUrl(searchParams.get('loginPageUrl'), '/login');
  const isUserNotFound = displayError === 'User not found';

  // Missing OTT error
  if (!ott && !backendError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-4">
        <h5 className="text-red-500 text-xl font-bold">Sign-in Failed</h5>
        <p className="text-gray-600 text-center">No SSO token provided</p>
        <AppButton
          text="Go to Login"
          onClick={() => {
            window.location.href = loginPageUrl;
          }}
        />
      </div>
    );
  }

  if (ssoLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <CircularProgress />
        <p className="text-center">Signing you in...</p>
      </div>
    );
  }

  if (displayError) {
    return (
      <div className="flex w-full h-full justify-center items-center px-4 py-8">
        <div className="flex flex-col w-full max-w-sm md:max-w-md lg:max-w-lg items-center justify-center">
          {supplierLogoUrl && (
            <div className="flex flex-col items-center mb-8 md:mb-14">
              <img className="max-h-16 md:max-h-20 w-auto" src={supplierLogoUrl} />
              <div className="flex items-center mt-2">
                <p className="text-sm">Powered By</p>
                <a href="https://usedill.com/" target="_blank">
                  <img className="h-6 md:h-8 ml-2" src={dillLogoWithText} />
                </a>
              </div>
            </div>
          )}
          {!supplierLogoUrl && (
            <div className="flex flex-col items-center mb-8 md:mb-14 w-full">
              <img className="w-3/4 max-w-xs md:max-w-sm" src={dillLogoWithText} />
            </div>
          )}

          {isUserNotFound ? (
            <div className="flex flex-col GREY_100-BG px-6 md:px-14 py-4 md:py-5 rounded-xl w-full">
              <div className="flex flex-col">
                <p className="text-center font-bold text-sm mb-2">
                  We see that you're a new user. Reach out to{' '}
                  <span className="PRIMARY_500-CLR font-bold cursor-pointer whitespace-nowrap">
                    (628) 304-0931
                  </span>{' '}
                  or{' '}
                  <span className="PRIMARY_500-CLR font-bold cursor-pointer whitespace-nowrap">
                    support@usedill.com
                  </span>{' '}
                  to request access to the portal
                </p>

                <AppButton
                  text="Go to Login"
                  buttonWidth="100%"
                  onClick={() => {
                    window.location.href = loginPageUrl;
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-4 p-4">
              <h5 className="text-red-500 text-xl font-bold">Sign-in Failed</h5>
              <p className="text-gray-600 text-center">{displayError}</p>
              <AppButton
                text="Go to Login"
                onClick={() => {
                  window.location.href = loginPageUrl;
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default SSOCompletePage;
